/**
 * OrganizationMembersTab
 * ======================
 *
 * Invite + manage ORGANIZATION members (Settings -> Organization
 * Members). This replaces the old workspace-members tab in Settings —
 * per-workspace grants now live on the workspace detail page, and can
 * only be given to people who are already organization members.
 *
 * What an org admin can do here:
 *   - Invite someone by email (role picked up front; the org's
 *     allowed-email-domains policy is enforced server-side and hinted
 *     in the form). The invitee gets an email; they join once they
 *     create their account and accept.
 *   - See every member with their role, last login, and the org
 *     workspaces they can access.
 *   - Change roles, remove members.
 *   - Assign members to workspaces (grants managed right here via the
 *     workspace-members API).
 *   - See + revoke pending invitations.
 *
 * Backend (via the ``organizations`` redux-saga namespace):
 *   GET    /organization/mine
 *   GET    /organization/<id>                    (policies -> domain hint)
 *   GET    /organization/<id>/members            (enriched: last login, workspaces)
 *   POST   /organization/<id>/invite
 *   PUT    /organization/<id>/members/<uid>/role
 *   DELETE /organization/<id>/members/<uid>
 *   GET    /organization/<id>/invitations?status=pending
 *   DELETE /organization/<id>/invitations/<inv>
 *   GET    /organization/<id>/workspaces
 *   POST   /workspace/<ws>/members               (assign)
 *   DELETE /workspace/<ws>/members/<uid>         (unassign)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  CircularProgress, Tooltip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, Divider,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import DnsIcon from '@mui/icons-material/Dns';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import { format } from '../../utils/datetime';
import {
  fetchRequest as fetchMine,
  clear as clearMine,
} from '../../redux/actions/organizations/mine';
import {
  fetchRequest as fetchMembers,
  clear as clearMembers,
} from '../../redux/actions/organizations/members';
import { fetchRequest as inviteRequest } from '../../redux/actions/organizations/invite';
import { fetchRequest as roleRequest } from '../../redux/actions/organizations/memberRole';
import { fetchRequest as removeRequest } from '../../redux/actions/organizations/memberRemove';
import { fetchRequest as revokeRequest } from '../../redux/actions/organizations/invitationRevoke';
import {
  fetchRequest as assignRequest,
  clear as clearAssign,
} from '../../redux/actions/organizations/workspaceAssign';

// Mirrors VALID_MEMBER_ROLES on the BE.
const MEMBER_ROLES = ['viewer', 'member', 'publisher', 'billing', 'admin'];
const WORKSPACE_ROLES = ['viewer', 'editor', 'admin'];

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const niceDate = (v) => {
  if (!v) return null;
  try { return format(new Date(v), 'PP'); } catch (_e) { return null; }
};

// ---------------------------------------------------------------------------
// Workspace-assignment dialog for one member
// ---------------------------------------------------------------------------

const WorkspaceAssignDialog = ({ open, member, workspaces, orgId, onClose }) => {
  const dispatch = useDispatch();
  const assignSlice = useSelector((s) => s.organization_workspace_assign) || {};
  const [busyId, setBusyId] = useState(null);
  const [role, setRole] = useState('editor');

  const memberWs = useMemo(
    () => new Map((member?.workspaces || []).map((w) => [w.uid, w])),
    [member],
  );

  const toggle = (ws, hasAccess) => {
    setBusyId(ws.uid);
    dispatch(assignRequest({
      orgId,
      workspaceId: ws.uid,
      userId: member.user_id,
      role,
      assign: !hasAccess,
    }));
  };

  // The saga refreshed the roster (or reported the error via snackbar) —
  // release the row.
  useEffect(() => {
    if (assignSlice.response || assignSlice.error) setBusyId(null);
  }, [assignSlice.response, assignSlice.error]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Workspace access — {member?.name || member?.email}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Role for new assignments:
          </Typography>
          <TextField
            select size="small" value={role}
            onChange={(e) => setRole(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            {WORKSPACE_ROLES.map((r) => (
              <MenuItem key={r} value={r}>{cap(r)}</MenuItem>
            ))}
          </TextField>
        </Stack>
        {workspaces.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            The organization has no workspaces yet.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableBody>
              {workspaces.map((ws) => {
                const row = memberWs.get(ws.uid);
                const isOwner = row?.role === 'owner';
                const hasAccess = Boolean(row);
                return (
                  <TableRow key={ws.uid} hover>
                    <TableCell padding="checkbox">
                      <Tooltip title={isOwner ? 'Owns this workspace' : ''}>
                        <span>
                          <Checkbox
                            size="small"
                            checked={hasAccess}
                            disabled={isOwner || busyId === ws.uid}
                            onChange={() => toggle(ws, hasAccess)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{ws.name}</TableCell>
                    <TableCell align="right">
                      {busyId === ws.uid ? (
                        <CircularProgress size={14} />
                      ) : row ? (
                        <Chip size="small" variant="outlined" label={cap(row.role)} />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

const OrganizationMembersTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mineSlice = useSelector((s) => s.organization_mine) || {};
  const membersSlice = useSelector((s) => s.organization_members) || {};
  const inviteSlice = useSelector((s) => s.organization_invite) || {};

  const [initialized, setInitialized] = useState(false);
  const [orgId, setOrgId] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const [assignTarget, setAssignTarget] = useState(null);

  // Discover the user's orgs (a failure here is silent: no org, no tab).
  useEffect(() => {
    dispatch(clearMembers());
    dispatch(clearAssign());
    dispatch(clearMine());
    dispatch(fetchMine());
    setInitialized(true);
  }, [dispatch]);

  const orgs = useMemo(
    () => (mineSlice.response?.organizations || []).filter((o) => o.is_team_plus),
    [mineSlice.response],
  );
  const loadedOrgs = initialized && !mineSlice.isLoading
    && Boolean(mineSlice.response || mineSlice.error);

  useEffect(() => {
    if (!loadedOrgs || orgId || !orgs.length) return;
    setOrgId(orgs[0].uid);
  }, [loadedOrgs, orgs, orgId]);

  const canManage = useMemo(
    () => Boolean(orgs.find((x) => x.uid === orgId)?.can_manage),
    [orgs, orgId],
  );

  // (Re)load the roster whenever the selected org changes. Mutation sagas
  // (invite / role / remove / revoke / assign) re-dispatch this themselves.
  useEffect(() => {
    if (!orgId) return;
    dispatch(fetchMembers({ orgId }));
  }, [orgId, dispatch]);

  const members = membersSlice.response?.members || [];
  const invitations = membersSlice.response?.invitations || [];
  const workspaces = membersSlice.response?.workspaces || [];
  const allowedDomains = membersSlice.response?.allowedDomains || [];
  const loading = Boolean(membersSlice.isLoading);
  const inviting = Boolean(inviteSlice.isLoading);

  // Invitation went out (snackbar comes from the saga) — reset the field.
  useEffect(() => {
    if (inviteSlice.response) setInviteEmail('');
  }, [inviteSlice.response]);

  const handleInvite = () => {
    const email = inviteEmail.trim();
    if (!email) { enqueueSnackbar('Enter an email', { variant: 'warning' }); return; }
    dispatch(inviteRequest({ orgId, email, role: inviteRole }));
  };

  const handleRoleChange = (member, role) => {
    dispatch(roleRequest({ orgId, userId: member.user_id, role }));
  };

  const handleRemove = (member) => {
    dispatch(removeRequest({ orgId, userId: member.user_id }));
  };

  const handleRevoke = (inv) => {
    dispatch(revokeRequest({ orgId, invitationId: inv.uid }));
  };

  if (!loadedOrgs || (orgs.length > 0 && !orgId)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
    );
  }

  // No org: the members surface is a Team+ feature.
  if (!orgId) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <GroupsIcon fontSize="small" color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Organization Members</Typography>
        </Stack>
        <Alert
          severity="info"
          icon={<LockIcon fontSize="inherit" />}
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/settings/billing')}>
              Upgrade
            </Button>
          )}
          sx={{ mt: 1.5 }}
        >
          Inviting teammates requires a <strong>Team</strong> plan or above
          (this creates your organization).
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <GroupsIcon fontSize="small" color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Organization Members</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Invite people to your organization and manage their roles and
        workspace access. Workspace-level grants live on each workspace's
        page and are limited to organization members.
      </Typography>

      {orgs.length > 1 && (
        <TextField
          select size="small" label="Organization" value={orgId}
          onChange={(e) => setOrgId(e.target.value)} sx={{ minWidth: 280, mb: 3 }}
        >
          {orgs.map((o) => <MenuItem key={o.uid} value={o.uid}>{o.name || o.uid}</MenuItem>)}
        </TextField>
      )}

      {/* Invite */}
      {canManage && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1 }} alignItems="flex-start">
            <TextField
              size="small"
              label="Email"
              placeholder="person@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            <TextField
              select size="small" label="Role" value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {MEMBER_ROLES.map((r) => (
                <MenuItem key={r} value={r}>{cap(r)}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={handleInvite}
              disabled={inviting}
              sx={{ mt: { xs: 0, sm: 0.25 } }}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            {allowedDomains.length > 0
              ? `This organization only allows members from: ${allowedDomains.join(', ')}.`
              : 'They\'ll get an email invitation and join once they accept.'}
          </Typography>
        </>
      )}

      {/* Members */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Last login</TableCell>
              <TableCell>Workspaces</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 && (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No members yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {members.map((m) => (
              <TableRow key={m.user_id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.name || m.email || m.user_id}
                  </Typography>
                  {m.name && (
                    <Typography variant="caption" color="text.secondary">
                      {m.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {m.is_owner ? (
                    <Chip size="small" label="Owner" color="primary" />
                  ) : canManage ? (
                    <TextField
                      select size="small" variant="standard" value={m.role || 'member'}
                      onChange={(e) => handleRoleChange(m, e.target.value)}
                      sx={{ minWidth: 110 }}
                    >
                      {MEMBER_ROLES.map((r) => (
                        <MenuItem key={r} value={r}>{cap(r)}</MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Chip size="small" variant="outlined" label={cap(m.role || 'member')} />
                  )}
                </TableCell>
                <TableCell>
                  {m.has_logged_in ? (
                    <Typography variant="body2">{niceDate(m.last_login_at) || '—'}</Typography>
                  ) : (
                    <Chip size="small" variant="outlined" label="Never" />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<DnsIcon sx={{ fontSize: 14 }} />}
                    onClick={() => canManage && setAssignTarget(m)}
                    disabled={!canManage}
                    sx={{ textTransform: 'none', minWidth: 0 }}
                  >
                    {m.workspaces_count ?? (m.workspaces || []).length}
                  </Button>
                </TableCell>
                {canManage && (
                  <TableCell align="right">
                    {m.is_owner ? (
                      <Tooltip title="The owner can't be removed">
                        <span>
                          <IconButton size="small" disabled>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <IconButton size="small" onClick={() => handleRemove(m)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <MailOutlineIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Pending invitations
            </Typography>
          </Stack>
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Invited by</TableCell>
                <TableCell>Expires</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((inv) => (
                <TableRow key={inv.uid} hover>
                  <TableCell>{inv.email}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={cap(inv.role || 'member')} />
                  </TableCell>
                  <TableCell>{inv.inviter_name || inv.inviter_email || '—'}</TableCell>
                  <TableCell>{niceDate(inv.expires_at) || '—'}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Tooltip title="Revoke invitation">
                        <IconButton size="small" onClick={() => handleRevoke(inv)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </>
      )}

      <WorkspaceAssignDialog
        open={!!assignTarget}
        member={assignTarget ? members.find((m) => m.user_id === assignTarget.user_id) || assignTarget : null}
        workspaces={workspaces}
        orgId={orgId}
        onClose={() => setAssignTarget(null)}
      />
    </Paper>
  );
};

export default OrganizationMembersTab;
